import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { formsApi } from "../../../services/api";
import { useTranslation } from "../../../contexts/TranslationContext";
import { ChartBarIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import DataTable, {
  Column,
  EmptyState,
  PaginationConfig,
} from "../../common/DataTable";
import Badge from "../../common/Badge";
import { FormVariation, FormSubmission } from "../../../types";

interface FormSubmissionsTableProps {
  formId: number;
  variations: FormVariation[];
  itemsPerPage?: number;
}

const FormSubmissionsTable: React.FC<FormSubmissionsTableProps> = ({
  formId,
  variations,
  itemsPerPage = 10,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);

  const { data: submissionsData, isLoading } = useQuery(
    ["form-submissions", formId, currentPage],
    () =>
      formsApi.getFormSubmissions(
        formId,
        (currentPage - 1) * itemsPerPage,
        itemsPerPage,
        "submitted_at",
        "desc",
      ),
    {
      enabled: !!formId,
      keepPreviousData: true, // Keep showing old data while fetching new page
    },
  );

  const syncMutation = useMutation(
    () => formsApi.syncFormSubmissionsPaymentStatus(formId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["form-submissions", formId]);
      },
    },
  );

  const handleRefresh = async () => {
    await syncMutation.mutateAsync();
  };

  const getStatusVariant = (
    status: string,
  ): "success" | "processing" | "warning" | "error" | "purple" => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "processing";
      case "expired":
        return "purple";
      case "canceled":
        return "warning";
      case "failed":
        return "error";
      default:
        return "error";
    }
  };

  const submissions = submissionsData?.items || [];
  const totalSubmissions = submissionsData?.total || 0;
  const totalPages = Math.ceil(totalSubmissions / itemsPerPage);

  const getSubmissionActions = useCallback(
    (submission: FormSubmission) => {
      let actions = [];
      if (submission.pipedrive_deal_id) {
        actions.push(
          <a
            href={submission.pipedrive_deal_url}
            target="_blank"
            rel="noopener noreferrer"
            title={t("forms.detail.submissions.showInPipedrive")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0"
                y="0"
                width="100"
                height="100"
                rx="25"
                ry="25"
                fill="#26292c"
              />

              <text
                x="50"
                y="65"
                font-family="sans-serif"
                font-size="75"
                font-weight="bold"
                text-anchor="middle"
                fill="#ffffff"
              >
                p
              </text>
            </svg>
          </a>,
        );
      }

      if (submission.stripe_payment_intent_id) {
        actions.push(
          <a
            href={
              "https://dashboard.stripe.com/payments/" +
              submission.stripe_payment_intent_id
            }
            target="_blank"
            rel="noopener noreferrer"
            title={t("forms.detail.submissions.showInStripe")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0"
                y="0"
                width="100"
                height="100"
                rx="25"
                ry="25"
                fill="#665BFE"
              />

              <text
                x="50"
                y="65"
                font-family="sans-serif"
                font-size="75"
                font-weight="bold"
                text-anchor="middle"
                fill="#ffffff"
              >
                s
              </text>
            </svg>
          </a>,
        );
      }

      return actions;
    },
    [t],
  );

  const columns: Column<FormSubmission>[] = useMemo(
    () => [
      {
        key: "customer",
        header: t("forms.detail.submissions.customer"),
        render: (submission) => (
          <div>
            <p className="text-sm font-medium text-gray-900">
              {submission.first_name} {submission.last_name}
            </p>
          </div>
        ),
      },
      {
        key: "variation",
        header: t("forms.detail.submissions.variation"),
        render: (submission) => {
          return (
            <span className="text-sm text-gray-900">
              {submission.variation_name || "Unknown"}
            </span>
          );
        },
      },
      {
        key: "paymentStatus",
        header: t("forms.detail.submissions.paymentStatus"),
        render: (submission) => (
          <Badge
            label={submission.payment_status}
            variant={getStatusVariant(submission.payment_status)}
          />
        ),
      },
      {
        key: "date",
        header: t("forms.detail.submissions.date"),
        render: (submission) => (
          <span className="text-sm text-gray-500">
            {new Date(submission.submitted_at).toLocaleDateString()}
          </span>
        ),
      },
      {
        key: "actions",
        header: t("forms.detail.submissions.actions"),
        render: (submission) => {
          return (
            <div className="text-sm text-gray-900 flex items-center space-x-2">
              {getSubmissionActions(submission) ?? "-"}
            </div>
          );
        },
      },
    ],
    [t, getSubmissionActions],
  );

  const emptyState: EmptyState = {
    icon: ChartBarIcon,
    title: t("forms.detail.submissions.noSubmissions"),
    description: t("forms.detail.submissions.noSubmissionsDescription"),
  };

  const pagination: PaginationConfig | undefined =
    totalPages > 1
      ? {
          currentPage,
          totalPages,
          totalItems: totalSubmissions,
          itemsPerPage,
          onPageChange: setCurrentPage,
        }
      : undefined;

  const refreshButton = (
    <button
      onClick={handleRefresh}
      disabled={syncMutation.isLoading}
      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label={t("forms.detail.submissions.syncTooltip")}
    >
      <ArrowPathIcon
        className={`h-5 w-5 mr-2 ${
          syncMutation.isLoading ? "animate-spin" : ""
        }`}
      />
      <span>
        {t(
          syncMutation.isLoading
            ? "forms.detail.submissions.syncing"
            : "forms.detail.submissions.sync",
        )}
      </span>
    </button>
  );

  return (
    <DataTable
      data={submissions}
      columns={columns}
      keyExtractor={(submission) => submission.id}
      title={t("forms.detail.submissions.title")}
      emptyState={emptyState}
      pagination={pagination}
      loading={isLoading}
      showLoadingInHeader={true}
      headerActions={refreshButton}
    />
  );
};

export default FormSubmissionsTable;
